/**
 * The shop, and what a child is wearing
 * ([Economy](../../../docs/ECONOMY.md#the-shop)).
 *
 * **Everything here is cosmetic.** Nothing bought changes a lesson, a reward or
 * how hard anything is, and a child who never opens this tab is not behind in
 * anything that matters. That is worth stating in the file too, because it is
 * the constraint that makes a shop in a children's app acceptable at all.
 *
 * The acorns are why this screen exists: a currency with nothing to spend it on
 * is a number going up, which is not a reward.
 */

import { type ForSale, type Slot, SLOTS, refusalText, refuse } from '@capytalk/core';
import { avatarSvg } from '@capytalk/tokens';
import { useEffect, useState } from 'react';
import { type Client, type Trouble, troubleFrom } from './api.js';
import ChestOdds from './ChestOdds.js';
import Sneakers from './Sneakers.js';
import Titles from './Titles.js';
import { Thing } from './Wardrobe.js';

/** What the child looks like now, for trying things on. */
interface Look {
  readonly species: string;
  readonly background: string;
  readonly worn: Readonly<
    Record<string, { itemId: string; dye: string | null } | null>
  >;
}

/** Until the loadout arrives, or on a server that will not send one. */
const PLAIN: Look = { species: 'fox', background: '#eaf2ff', worn: {} };

/**
 * Try it on before buying it.
 *
 * The child's own animal, in everything they already wear, with this one thing
 * swapped in — so "is this the hat for me?" is answered by looking rather than
 * by spending acorns and finding out. Nothing is saved: closing this leaves the
 * outfit exactly as it was.
 */
function TryOn({
  item,
  look,
  acorns,
  busy,
  onBuy,
  onClose,
}: {
  readonly item: ForSale;
  readonly look: Look;
  readonly acorns: number;
  readonly busy: boolean;
  readonly onBuy: (id: string) => void;
  readonly onClose: () => void;
}) {
  const no = refuse(item, acorns);
  return (
    <div
      className="card"
      role="dialog"
      aria-label={`Trying on ${item.name}`}
      style={{ marginTop: 8, width: '100%', textAlign: 'center' }}
    >
      <b>
        {item.name} <span aria-hidden="true">{RARITY_MARK[item.rarity] ?? ''}</span>
      </b>
      <div style={{ display: 'grid', placeItems: 'center', marginTop: 6 }}>
        <svg
          viewBox="0 0 160 240"
          width={150}
          height={225}
          role="img"
          aria-label={`Your ${look.species} wearing the ${item.name}`}
          // Local, generated, CI-checked markup keyed by ids — never anything a
          // response chose (see Wardrobe).
          dangerouslySetInnerHTML={{
            __html: avatarSvg({
              species: look.species,
              background: look.background,
              loadout: { ...look.worn, [item.slot]: { itemId: item.id, dye: null } },
              uid: 'try',
            }),
          }}
        />
      </div>
      {item.owned ? (
        <p className="tiny" style={{ marginTop: 6 }}>
          You have this one! Wear it in your wardrobe.
        </p>
      ) : (
        <>
          {no === 'not-enough-acorns' && (
            <p className="tiny" style={{ marginTop: 6 }}>
              {refusalText['not-enough-acorns']}
            </p>
          )}
          <button
            className="btn"
            type="button"
            style={{ marginTop: 8 }}
            disabled={no !== null || busy}
            onClick={() => {
              onBuy(item.id);
            }}
          >
            Buy it for <span aria-hidden="true">🌰</span> {item.price}
          </button>
        </>
      )}
      <button
        className="btn ghost"
        type="button"
        style={{ marginTop: 8 }}
        onClick={onClose}
      >
        Back to the shop
      </button>
    </div>
  );
}

/** What each slot is called, in the words on the tab. */
const SLOT_NAMES: Readonly<Record<Slot, string>> = {
  hat: 'Hats',
  glasses: 'Glasses',
  top: 'Tops',
  neck: 'Necks',
  shoes: 'Shoes',
  held: 'To hold',
};

const RARITY_MARK: Readonly<Record<string, string>> = {
  common: '',
  rare: '✨',
  epic: '🌟',
  seasonal: '❄️',
};

function Item({
  item,
  acorns,
  onBuy,
  onTry,
  busy,
}: {
  readonly item: ForSale;
  readonly acorns: number;
  readonly onBuy: (id: string) => void;
  readonly onTry: (id: string) => void;
  readonly busy: boolean;
}) {
  // The same rule the server decides with, so a button is never offered for
  // something that is going to be refused.
  const no = refuse(item, acorns);

  return (
    <div className="card" style={{ marginTop: 8, width: '100%', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* What it looks like, before anything is spent. */}
        <Thing slot={item.slot} itemId={item.id} dye={null} size={40} />
        <b style={{ flex: 1 }}>
          {item.name} <span aria-hidden="true">{RARITY_MARK[item.rarity] ?? ''}</span>
        </b>
        {item.owned ? (
          <span className="tiny">Yours</span>
        ) : (
          <button
            className="btn"
            type="button"
            disabled={no !== null || busy}
            onClick={() => {
              onBuy(item.id);
            }}
          >
            <span aria-hidden="true">🌰</span> {item.price}
          </button>
        )}
      </div>
      <button
        className="chip"
        type="button"
        style={{ marginTop: 6 }}
        aria-label={`Try on ${item.name}`}
        onClick={() => {
          onTry(item.id);
        }}
      >
        <span aria-hidden="true">👀</span> Try it on
      </button>
      {no === 'not-enough-acorns' && (
        // Said before they tap, not after. A button that explains itself only
        // once it has been pressed is a button that teaches nothing.
        <p className="tiny" style={{ marginTop: 4 }}>
          {refusalText['not-enough-acorns']}
        </p>
      )}
    </div>
  );
}

export default function Shop({ client }: { readonly client: Client }) {
  const [items, setItems] = useState<readonly ForSale[]>([]);
  const [acorns, setAcorns] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [about, setAbout] = useState(false);
  /** The titles screen, mockup 25 (D-159). */
  const [titles, setTitles] = useState(false);
  /** The thing being tried on, if any. */
  const [trying, setTrying] = useState<string | null>(null);
  const [look, setLook] = useState<Look>(PLAIN);

  useEffect(() => {
    let live = true;
    // Only for trying things on, so a loadout that will not load costs the
    // preview its outfit and nothing else.
    Promise.resolve()
      .then(() => client.shop.loadout.query())
      .then((loadout) => {
        if (!live) return;
        setLook({
          species: loadout.species ?? PLAIN.species,
          background: loadout.background ?? PLAIN.background,
          worn: loadout.worn ?? {},
        });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve` because a server older than this app has no
    // `shop` router at all and the call *throws* rather than rejecting (D-066).
    Promise.resolve()
      .then(() => client.shop.shelf.query())
      .then((shelf) => {
        if (!live) return;
        setItems(shelf.items);
        setAcorns(shelf.acorns);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client]);

  const onBuy = (itemId: string) => {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    Promise.resolve()
      .then(() => client.shop.buy.mutate({ itemId }))
      .then((bought) => {
        setAcorns(bought.acorns);
        if (bought.refused === null) {
          setItems((was) =>
            was.map((one) => (one.id === itemId ? { ...one, owned: true } : one)),
          );
          setSaid('Yours!');
        } else {
          setSaid(refusalText[bought.refused]);
        }
      })
      .catch(() => {
        // Nothing was spent — the charge is the first thing the server does and
        // it either happened or it did not. Saying so beats a silent no-op.
        setSaid('That did not work. Try again in a moment.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (titles) {
    return (
      <Titles
        client={client}
        onDone={() => {
          setTitles(false);
          // Buying a title spends acorns the shelf here is showing.
          void Promise.resolve()
            .then(() => client.shop.shelf.query())
            .then((shelf) => {
              setAcorns(shelf.acorns);
            })
            .catch(() => undefined);
        }}
      />
    );
  }

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          The shop would not load. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="grow pad">
      <Sneakers
        text={said ?? 'Spend your acorns on something silly!'}
        // No recording for anything he says here, so the device's own voice
        // reads it — the normal path everywhere (D-064).
        audioRef={null}
        size={72}
      />

      <p className="sub" style={{ marginTop: 8 }}>
        <span aria-hidden="true">🌰</span> {acorns} acorns
      </p>

      {loaded && items.length === 0 && (
        <p className="sub">Nothing in the shop yet. Come back soon!</p>
      )}

      {SLOTS.map((slot) => {
        const inSlot = items.filter((one) => one.slot === slot);
        if (inSlot.length === 0) return null;
        return (
          <section key={slot} style={{ marginTop: 14 }}>
            <h3>{SLOT_NAMES[slot]}</h3>
            {inSlot.map((item) =>
              item.id === trying ? (
                <TryOn
                  key={item.id}
                  item={item}
                  look={look}
                  acorns={acorns}
                  busy={busy}
                  onBuy={onBuy}
                  onClose={() => {
                    setTrying(null);
                  }}
                />
              ) : (
                <Item
                  key={item.id}
                  item={item}
                  acorns={acorns}
                  onBuy={onBuy}
                  onTry={setTrying}
                  busy={busy}
                />
              ),
            )}
          </section>
        );
      })}

      <section style={{ marginTop: 14 }}>
        <h3>Titles</h3>
        <p className="tiny" style={{ marginTop: 4 }}>
          Words under your name. Save up for one, or earn one free.
        </p>
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 8 }}
          onClick={() => {
            setTitles(true);
          }}
        >
          <span aria-hidden="true">🏅</span> See the titles
        </button>
      </section>

      {/*
        Here as well as on the chest: a chest on the recap is gone once it is
        opened, and the shop is where a child goes to wonder whether to save up
        or hope ([D-021](../../../docs/DECISIONS.md#d-021)).
      */}
      {about ? (
        <ChestOdds
          client={client}
          onClose={() => {
            setAbout(false);
          }}
        />
      ) : (
        <button
          className="chip"
          type="button"
          style={{ marginTop: 16 }}
          onClick={() => {
            setAbout(true);
          }}
        >
          How do chests work?
        </button>
      )}
    </div>
  );
}
